import { useEffect, useRef } from 'react';
import { useQueryState, useMutation, useAppUI } from '@ywwwtseng/react-kit';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import type { SelectUser } from '@/types';

export function useSyncWalletAddress() {
  const { loadingUI, showLoadingUI } = useAppUI();
  const updatedMeTimer = useRef<NodeJS.Timeout | null>(null);
  const isLoading = useRef(false);
  const { accounts, disconnect, open, executeConnectedCallbacks } = useWalletKitConnect();
  const { mutate } = useMutation('me:update', {
    onError: async () => {
      await disconnect(true);
      await open();
    }
  });
  const me = useQueryState<SelectUser>('me');


  useEffect(() => {
    const updates: Record<string, string | null> = {};
    if (isLoading.current) {
      return;
    }

    if (!me) {
      return;
    }

    if (accounts.bsc.status === 'connected') {
      if (accounts.bsc.address && me.bsc_address !== accounts.bsc.address) {
        updates.bsc_address = accounts.bsc.address;
      }
    } else if (accounts.bsc.status === 'disconnected') {
      if (me.bsc_address) {
        updates.bsc_address = null;
      }
    }

    if (accounts.solana.status === 'connected') {
      if (accounts.solana.address && me.sol_address !== accounts.solana.address) {
        updates.sol_address = accounts.solana.address;
      }
    } else if (accounts.solana.status === 'disconnected') {
      if (me.sol_address) {
        updates.sol_address = null;
      }
    }

    if (accounts.ethereum.status === 'connected') {
      if (accounts.ethereum.address && me.eth_address !== accounts.ethereum.address) {
        updates.eth_address = accounts.ethereum.address;
      }
    } else if (accounts.ethereum.status === 'disconnected') {
      if (me.eth_address) {
        updates.eth_address = null;
      }
    }

    if (updatedMeTimer.current) {
      clearTimeout(updatedMeTimer.current);
      updatedMeTimer.current = null;
      showLoadingUI(false);
    }

    if (Object.keys(updates).length > 0) {
      showLoadingUI(true);

      updatedMeTimer.current = setTimeout(() => {
        isLoading.current = true;
        void mutate(updates)
          .then(() => {
            if (updates.bsc_address) {
              executeConnectedCallbacks('bsc');
            }
          })
          .finally(() => {
            updatedMeTimer.current = null;
            isLoading.current = false;
            showLoadingUI(false);
          });
      }, 800);
    }
  }, [JSON.stringify(accounts), JSON.stringify(me)]);
}
